import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('click', this.#handleImageClick);
    this.refs.dialog.addEventListener('mousedown', this.#handleDragStart);
    window.addEventListener('resize', this.#scheduleImageMeasure);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('click', this.#handleImageClick);
    this.refs.dialog.removeEventListener('mousedown', this.#handleDragStart);
    window.removeEventListener('resize', this.#scheduleImageMeasure);
  }

  /**
   * Measures the most-visible image's rendered width and exposes it on the
   * dialog as `--lightbox-image-w` so the navigation arrows can sit flush
   * against the image edges instead of the viewport edges.
   */
  #scheduleImageMeasure = () => {
    requestAnimationFrame(() => this.#measureImage());
  };

  async #measureImage() {
    const { media, dialog } = this.refs;
    if (!media || !media.length) return;
    const visible = await getMostVisibleElement(media);
    const img = visible?.querySelector('img.product-media__image');
    if (!(img instanceof HTMLImageElement)) return;
    const rect = img.getBoundingClientRect();
    if (rect.width > 0) {
      dialog.style.setProperty('--lightbox-image-w', `${Math.round(rect.width)}px`);
    }
  }

  /**
   * Exit deep-zoom mode: clear scale + translate from any zoomed wrapper and
   * remove the dialog-level class so arrows / thumbnails / X come back.
   */
  exitDeepZoom() {
    const { dialog } = this.refs;
    dialog.querySelectorAll('.product-media__drag-zoom-wrapper.is-deep-zoomed').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.classList.remove('is-deep-zoomed');
      el.style.transformOrigin = '';
      el.style.removeProperty('--deep-zoom-tx');
      el.style.removeProperty('--deep-zoom-ty');
      el.removeAttribute('data-dragging');
    });
    dialog.classList.remove('is-deep-zoomed');
  }

  /**
   * Click + drag panning when the image is in deep-zoom mode. Translation is
   * stored as CSS variables on the wrapper so the existing scale transform
   * stays composable.
   */
  #dragState = null;

  #suppressNextClick = false;

  #handleDragStart = (event) => {
    if (window.innerWidth < 750) return;
    if (event.button !== 0) return;
    if (!(event.target instanceof Element)) return;
    const wrapper = event.target.closest('.product-media__drag-zoom-wrapper.is-deep-zoomed');
    if (!(wrapper instanceof HTMLElement)) return;
    event.preventDefault();

    const tx = parseFloat(getComputedStyle(wrapper).getPropertyValue('--deep-zoom-tx')) || 0;
    const ty = parseFloat(getComputedStyle(wrapper).getPropertyValue('--deep-zoom-ty')) || 0;
    this.#dragState = {
      wrapper,
      startX: event.clientX,
      startY: event.clientY,
      startTx: tx,
      startTy: ty,
      hasMoved: false,
    };
    wrapper.setAttribute('data-dragging', 'true');
    window.addEventListener('mousemove', this.#handleDragMove);
    window.addEventListener('mouseup', this.#handleDragEnd, { once: true });
  };

  #handleDragMove = (event) => {
    if (!this.#dragState) return;
    const { wrapper, startX, startY, startTx, startTy } = this.#dragState;
    const dxRaw = event.clientX - startX;
    const dyRaw = event.clientY - startY;
    if (Math.abs(dxRaw) > 3 || Math.abs(dyRaw) > 3) {
      this.#dragState.hasMoved = true;
    }
    // Inside a scale(2.5) transform — divide deltas so the cursor tracks
    // the image content rather than over-shooting.
    const dx = dxRaw / 2.5;
    const dy = dyRaw / 2.5;
    wrapper.style.setProperty('--deep-zoom-tx', `${startTx + dx}px`);
    wrapper.style.setProperty('--deep-zoom-ty', `${startTy + dy}px`);
  };

  #handleDragEnd = () => {
    if (this.#dragState) {
      this.#dragState.wrapper?.removeAttribute('data-dragging');
      // If the user actually moved while holding, swallow the click that the
      // browser will dispatch on mouseup so it doesn't toggle deep-zoom off.
      if (this.#dragState.hasMoved) {
        this.#suppressNextClick = true;
      }
    }
    this.#dragState = null;
    window.removeEventListener('mousemove', this.#handleDragMove);
  };

  /**
   * Returns the index of the currently most-visible media element.
   */
  async #currentIndex() {
    const { media } = this.refs;
    if (!media || !media.length) return 0;
    const mostVisible = await getMostVisibleElement(media);
    const idx = media.indexOf(mostVisible);
    return idx === -1 ? 0 : idx;
  }

  /**
   * Navigate to the next image.
   */
  async next() {
    const { media } = this.refs;
    if (!media || !media.length) return;
    const current = await this.#currentIndex();
    const target = (current + 1) % media.length;
    this.selectThumbnail(target);
  }

  /**
   * Navigate to the previous image.
   */
  async previous() {
    const { media } = this.refs;
    if (!media || !media.length) return;
    const current = await this.#currentIndex();
    const target = (current - 1 + media.length) % media.length;
    this.selectThumbnail(target);
  }

  /**
   * Desktop deep-zoom toggle. Clicking the image enlarges it via CSS scale,
   * setting transform-origin to the click coordinates so the area under the
   * cursor stays visible. Clicking again resets. Mobile (pinch zoom via
   * drag-zoom-wrapper) is unaffected.
   */
  #handleImageClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // After a drag we get a click event on mouseup — discard it so deep-zoom
    // doesn't toggle off when the user simply releases the mouse.
    if (this.#suppressNextClick) {
      this.#suppressNextClick = false;
      return;
    }

    // Clicks on the close button, arrows, or backdrop area should pass through
    // to whichever handler manages them.
    const target = /** @type {Element} */ (event.target);
    if (target.closest('.dialog-zoomed-gallery__nav')) return;
    if (target.closest('.dialog-zoomed-gallery__close-button')) return;
    if (target.closest('.dialog-zoomed-gallery__deep-close')) return;
    if (target.closest('.dialog-thumbnails-list-container')) return;
    if (target.closest('.dialog-thumbnails-list__thumbnail')) return;

    if (window.innerWidth < 750) {
      // Mobile: pinch-zoom via drag-zoom-wrapper handles image interactions.
      // We only need to handle "click the empty area to close".
      if (target.closest('.product-media__drag-zoom-wrapper')) return;
      this.close();
      return;
    }

    // Desktop: clicking the image toggles deep-zoom; clicking the dark area
    // (anything that isn't the image element or an interactive UI control,
    // which already returned above) closes the lightbox.
    if (!(target instanceof HTMLImageElement)) {
      this.close();
      return;
    }

    const wrapper = target.closest('.product-media__drag-zoom-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    event.stopPropagation();

    const isZoomed = wrapper.classList.toggle('is-deep-zoomed');
    if (isZoomed) {
      const rect = wrapper.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      wrapper.style.transformOrigin = `${x}% ${y}%`;
    } else {
      wrapper.style.transformOrigin = '';
    }
    this.refs.dialog.classList.toggle('is-deep-zoomed', isZoomed);
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
    this.#scheduleImageMeasure();
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
    this.#scheduleImageMeasure();
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
